import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { getQueryFn } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, RefreshCw } from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/format";

type InvoiceStatusFilter = "all" | "draft" | "pending" | "approved" | "paid" | "voided";
type XeroStatusFilter = "all" | "not_synced" | "queued" | "synced" | "failed";

interface InvoiceListItem {
  id: string;
  clientName: string;
  revenueOwnerName: string;
  status: string;
  xeroSyncStatus: string;
  revenueMonth: string;
  totalAmount: string;
  currency: string;
  issuedAt: string;
}

export default function InvoicesPage() {
  const [, navigate] = useLocation();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<InvoiceStatusFilter>("all");
  const [xeroFilter, setXeroFilter] = useState<XeroStatusFilter>("all");

  const { data: invoices, isLoading, refetch, isFetching } = useQuery<
    InvoiceListItem[]
  >({
    queryKey: ["/api/invoices"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const filteredInvoices = useMemo(() => {
    return (invoices ?? []).filter((invoice) => {
      const matchesSearch =
        !search.trim() ||
        invoice.clientName.toLowerCase().includes(search.toLowerCase()) ||
        invoice.revenueOwnerName.toLowerCase().includes(search.toLowerCase()) ||
        invoice.revenueMonth.toLowerCase().includes(search.toLowerCase()) ||
        invoice.id.toLowerCase().includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || invoice.status === statusFilter;
      const matchesXero =
        xeroFilter === "all" || invoice.xeroSyncStatus === xeroFilter;

      return matchesSearch && matchesStatus && matchesXero;
    });
  }, [invoices, search, statusFilter, xeroFilter]);

  const columns: ColumnDef<InvoiceListItem>[] = [
    {
      key: "client",
      header: "Client",
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.clientName}</span>
          <span className="text-xs text-muted-foreground">{row.revenueOwnerName}</span>
        </div>
      ),
    },
    {
      key: "revenueMonth",
      header: "Revenue Month",
      cell: (row) => row.revenueMonth,
    },
    {
      key: "issuedAt",
      header: "Issued",
      cell: (row) => formatDate(row.issuedAt),
    },
    {
      key: "totalAmount",
      header: "Total",
      cell: (row) => formatCurrency(row.totalAmount, row.currency),
    },
    {
      key: "status",
      header: "Invoice",
      cell: (row) => <StatusBadge type="invoice" status={row.status} />,
    },
    {
      key: "xeroSyncStatus",
      header: "Xero",
      cell: (row) => <StatusBadge type="xero" status={row.xeroSyncStatus} />,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Invoices"
        description="Review invoice snapshots, revenue attribution, and Xero sync state."
        action={
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={filteredInvoices}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/invoices/${row.id}`)}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search invoices..."
        filterSlot={
          <div className="flex flex-wrap gap-2">
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as InvoiceStatusFilter)}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Invoice status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Invoice Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="voided">Voided</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={xeroFilter}
              onValueChange={(value) => setXeroFilter(value as XeroStatusFilter)}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Xero status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Xero Statuses</SelectItem>
                <SelectItem value="not_synced">Not Synced</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="synced">Synced</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        emptyIcon={FileText}
        emptyTitle="No invoices"
        emptyDescription="Invoices will appear here once finance reporting starts generating them."
      />
    </div>
  );
}
